import {
  Box,
  Checkbox,
  Flex,
  Input,
  Menu,
  MenuButton,
  MenuList,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { FiArrowDown, FiArrowUp, FiFilter } from 'react-icons/fi';
import { FixedSizeList, ListOnItemsRenderedProps } from 'react-window';

import apiService from 'services';
import { accent, font, fontSize, neutral, radius, text } from 'styles/revampTokens';

import {
  buildCombinedFilterParameters,
  buildFacetFilterParameters,
  buildScopeFilterParameters,
  checkedValuesForFacetMenu,
  chooseColumnFilterFromFacetSelection,
  columnLabel,
  columnsQueryParameter,
  resolveDisplayColumns,
  sortParameter,
  userFilterChipText,
  type DataTableColumnSpec,
  type DataTableQueryState,
  type DataTableScopeFilterSpec,
  type TaskDimensionContext,
  type UserColumnFilter,
  type UserColumnFilters,
} from './taskDataTableQuery';

const ROW_HEIGHT = 36;
const HEADER_HEIGHT = 40;
const PAGE_SIZE = 100;
/** Minimum scroll body so the grid is usable on short viewports. */
const MIN_BODY_HEIGHT_PX = 400;

interface QueryPage {
  rows: Record<string, unknown>[];
  total_rows: number | null;
  has_more: boolean;
  columns: string[];
}

export interface TaskDataTableProperties {
  /** modelAPI data source id backing this data table. */
  dataTableId: number;
  label?: string | null;
  scopeFilters: DataTableScopeFilterSpec[];
  specColumns: DataTableColumnSpec[];
  missingHeader: boolean;
  task: TaskDimensionContext;
}

const formatCell = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  return String(value);
};

interface ColumnFilterMenuProperties {
  column: string;
  label: string;
  filter: UserColumnFilter | undefined;
  facetFilters: string[];
  dataTableId: number;
  missingHeader: boolean;
  onApply: (filter: UserColumnFilter | null) => void;
}

/** Excel-style value list for one column (server-backed distinct values). */
const ColumnFilterMenu = ({
  column,
  label,
  filter,
  facetFilters,
  dataTableId,
  missingHeader,
  onApply,
}: ColumnFilterMenuProperties) => {
  const [draft, setDraft] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const valuesQuery = useQuery(
    ['task-data-table-values', dataTableId, column, facetFilters.join('|'), missingHeader],
    async () => {
      const response = await apiService.getDataSourceColumnValues(dataTableId, column, {
        filter: facetFilters,
        limit: 500,
        ...(missingHeader ? { missing_header: true } : {}),
      });
      return (response.data?.values ?? []) as string[];
    },
    { enabled: open, staleTime: 30_000 },
  );

  const values = valuesQuery.data ?? [];
  const visible = values.filter((value) =>
    value.toLowerCase().includes(search.trim().toLowerCase()),
  );

  const toggle = (value: string) => {
    setDraft((current) =>
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value],
    );
  };

  useEffect(() => {
    if (!open || values.length === 0) return;
    setDraft(checkedValuesForFacetMenu(filter, values));
  }, [open, filter, values]);

  return (
    <Menu
      isOpen={open}
      onOpen={() => {
        setSearch('');
        setOpen(true);
      }}
      onClose={() => setOpen(false)}
    >
      <MenuButton
        as={Box}
        aria-label={`Filter ${label}`}
        display='inline-flex'
        alignItems='center'
        ml={1}
        p='2px'
        borderRadius='4px'
        cursor='pointer'
        color={filter ? accent.solid : text.muted}
        _hover={{ bg: neutral.surfaceHover }}
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
      >
        <FiFilter size={12} />
      </MenuButton>
      <MenuList
        minW='260px'
        maxH='320px'
        overflow='auto'
        p={3}
        onClick={(event) => event.stopPropagation()}
      >
        <Text fontSize={fontSize.label} fontWeight={600} color={text.secondary} mb={2}>
          Filter {label}
        </Text>
        <Input
          size='sm'
          placeholder='Search values'
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          mb={2}
        />
        {valuesQuery.isLoading && (
          <Flex align='center' gap={2} py={2} color={text.secondary}>
            <Spinner size='xs' />
            <Text fontSize={fontSize.label}>Loading values…</Text>
          </Flex>
        )}
        {!valuesQuery.isLoading &&
          visible.map((value) => (
            <Checkbox
              key={value}
              isChecked={draft.includes(value)}
              size='sm'
              mb={1}
              onChange={() => toggle(value)}
            >
              <Text fontSize={fontSize.body} noOfLines={1} title={value}>
                {value}
              </Text>
            </Checkbox>
          ))}
        <Flex gap={2} mt={3}>
          <Box
            as='button'
            type='button'
            fontSize={fontSize.label}
            color={accent.solid}
            onClick={() => {
              onApply(chooseColumnFilterFromFacetSelection(values, draft));
              setOpen(false);
            }}
          >
            Apply
          </Box>
          <Box
            as='button'
            type='button'
            fontSize={fontSize.label}
            color={text.secondary}
            onClick={() => {
              onApply(null);
              setOpen(false);
            }}
          >
            Clear
          </Box>
        </Flex>
      </MenuList>
    </Menu>
  );
};

/**
 * Read-only drill-down to rows in a model data table (modelAPI data source) inside a task step.
 * Scope filters from the workspace spec are always applied server-side and hidden from the UI.
 */
const TaskDataTable = ({
  dataTableId,
  label,
  scopeFilters,
  specColumns,
  missingHeader,
  task,
}: TaskDataTableProperties) => {
  const scopeFilterParameters = useMemo(
    () => buildScopeFilterParameters(scopeFilters, task),
    [scopeFilters, task],
  );
  const [userFilters, setUserFilters] = useState<UserColumnFilters>({});
  const [sort, setSort] = useState<DataTableQueryState['sort']>(null);
  const [displayColumns, setDisplayColumns] = useState<DataTableColumnSpec[]>(specColumns);
  const listReference = useRef<FixedSizeList>(null);
  const bodyReference = useRef<HTMLDivElement>(null);
  const [listHeight, setListHeight] = useState(MIN_BODY_HEIGHT_PX);

  useEffect(() => {
    setDisplayColumns(specColumns);
  }, [specColumns, dataTableId]);

  useEffect(() => {
    const node = bodyReference.current;
    if (!node) return () => {};
    const observer = new ResizeObserver(([entry]) => {
      setListHeight(Math.max(MIN_BODY_HEIGHT_PX, Math.floor(entry.contentRect.height)));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const queryState: DataTableQueryState = useMemo(
    () => ({ scopeFilters: scopeFilterParameters, userFilters, sort }),
    [scopeFilterParameters, userFilters, sort],
  );

  const filterKey = buildCombinedFilterParameters(queryState).join('|');
  const columnParameter = columnsQueryParameter(
    displayColumns.length > 0 ? displayColumns : specColumns,
  );

  const rowsQuery = useInfiniteQuery(
    [
      'task-data-table-rows',
      dataTableId,
      filterKey,
      sortParameter(sort),
      columnParameter,
      missingHeader,
    ],
    async ({ pageParam: pageParameter = 0 }) => {
      const response = await apiService.queryDataSourceLatest(dataTableId, {
        offset: pageParameter,
        limit: PAGE_SIZE,
        sort: sortParameter(sort),
        filter: buildCombinedFilterParameters(queryState),
        ...(columnParameter ? { columns: columnParameter } : {}),
        ...(missingHeader ? { missing_header: true } : {}),
      });
      const payload = response.data as QueryPage & { rows: Record<string, unknown>[] };
      return {
        rows: payload.rows ?? [],
        total_rows: payload.total_rows ?? null,
        has_more: Boolean(payload.has_more),
        columns: payload.columns ?? [],
        nextOffset: payload.has_more ? pageParameter + PAGE_SIZE : undefined,
      };
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextOffset,
      keepPreviousData: true,
    },
  );

  const flatRows = useMemo(
    () => rowsQuery.data?.pages.flatMap((page) => page.rows) ?? [],
    [rowsQuery.data],
  );

  const totalRows = rowsQuery.data?.pages[0]?.total_rows ?? flatRows.length;
  const columns = useMemo(() => {
    const responseCols = rowsQuery.data?.pages[0]?.columns ?? [];
    return resolveDisplayColumns(
      displayColumns.length > 0 ? displayColumns : specColumns,
      responseCols,
    );
  }, [displayColumns, specColumns, rowsQuery.data]);

  useEffect(() => {
    const responseCols = rowsQuery.data?.pages[0]?.columns ?? [];
    if (specColumns.length === 0 && responseCols.length > 0 && displayColumns.length === 0) {
      setDisplayColumns(resolveDisplayColumns([], responseCols));
    }
  }, [rowsQuery.data, specColumns.length, displayColumns.length]);

  const cycleSort = (column: string) => {
    setSort((current) => {
      if (current?.column !== column) return { column, direction: 'asc' };
      if (current.direction === 'asc') return { column, direction: 'desc' };
      return null;
    });
  };

  const onItemsRendered = useCallback(
    ({ visibleStopIndex }: ListOnItemsRenderedProps) => {
      if (
        visibleStopIndex >= flatRows.length - 25 &&
        rowsQuery.hasNextPage &&
        !rowsQuery.isFetchingNextPage
      ) {
        rowsQuery.fetchNextPage();
      }
    },
    [flatRows.length, rowsQuery],
  );

  const activeUserFilters = Object.entries(userFilters).filter(
    ([, columnFilter]) => columnFilter.values.length > 0,
  );

  const visibleListHeight =
    flatRows.length > 0 ? Math.min(listHeight, flatRows.length * ROW_HEIGHT) : listHeight;

  const Row = ({ index, style }: { index: number; style: CSSProperties }) => {
    const row = flatRows[index];
    return (
      <Flex
        style={style}
        borderBottom={`1px solid ${neutral.border}`}
        bg={index % 2 === 0 ? neutral.surface : neutral.surfaceMuted}
        align='center'
      >
        {columns.map((column, columnIndex) => (
          <Box
            key={column.name}
            flex={`0 0 ${columnIndex === 0 ? 200 : 140}px`}
            px={3}
            fontSize={fontSize.body}
            fontFamily={font.body}
            color={text.primary}
            whiteSpace='nowrap'
            overflow='hidden'
            textOverflow='ellipsis'
            title={formatCell(row?.[column.name])}
          >
            {formatCell(row?.[column.name])}
          </Box>
        ))}
      </Flex>
    );
  };

  return (
    <Box
      display='flex'
      flexDirection='column'
      flex='1'
      minH={{ base: '520px', lg: '560px' }}
      h='100%'
      sx={{ minHeight: 'max(520px, calc(100vh - 320px))' }}
      border={`1px solid ${neutral.border}`}
      borderRadius={radius.surface}
      overflow='hidden'
      bg={neutral.surface}
    >
      {label && (
        <Text px={4} py={2} fontSize={fontSize.label} fontWeight={600} color={text.secondary}>
          {label}
        </Text>
      )}
      {activeUserFilters.length > 0 && (
        <Flex px={4} py={2} gap={2} wrap='wrap' borderBottom={`1px solid ${neutral.border}`}>
          {activeUserFilters.map(([column, columnFilter]) => (
            <Flex
              key={column}
              align='center'
              gap={1}
              px={2}
              py='2px'
              borderRadius='4px'
              bg={accent.subtle}
              fontSize='11px'
              color={text.primary}
            >
              <Text fontWeight={600}>{columnLabel(columns, column)}:</Text>
              <Text noOfLines={1}>{userFilterChipText(columnFilter)}</Text>
              <Box
                as='button'
                type='button'
                ml={1}
                color={text.secondary}
                onClick={() =>
                  setUserFilters((current) => {
                    const next = { ...current };
                    delete next[column];
                    return next;
                  })
                }
              >
                ×
              </Box>
            </Flex>
          ))}
          <Box
            as='button'
            type='button'
            fontSize='11px'
            color={accent.solid}
            onClick={() => setUserFilters({})}
          >
            Clear all
          </Box>
        </Flex>
      )}
      <Flex
        minH={`${HEADER_HEIGHT}px`}
        bg={neutral.surfaceMuted}
        borderBottom={`1px solid ${neutral.border}`}
        align='stretch'
        overflow='auto'
        flexShrink={0}
      >
        {columns.map((column) => {
          const isSorted = sort?.column === column.name;
          const columnFilter = userFilters[column.name];
          return (
            <Flex
              key={column.name}
              flex={`0 0 ${column.name === columns[0]?.name ? 200 : 140}px`}
              align='center'
              px={3}
              cursor='pointer'
              onClick={() => cycleSort(column.name)}
              _hover={{ bg: neutral.surfaceHover }}
            >
              <Text
                fontSize={fontSize.label}
                fontWeight={600}
                color={text.primary}
                noOfLines={1}
                flex='1'
              >
                {columnLabel(columns, column.name)}
              </Text>
              {isSorted &&
                (sort?.direction === 'desc' ? (
                  <FiArrowDown size={12} color={accent.solid} />
                ) : (
                  <FiArrowUp size={12} color={accent.solid} />
                ))}
              <ColumnFilterMenu
                column={column.name}
                label={columnLabel(columns, column.name)}
                filter={columnFilter}
                facetFilters={buildFacetFilterParameters(queryState, column.name)}
                dataTableId={dataTableId}
                missingHeader={missingHeader}
                onApply={(next) =>
                  setUserFilters((current) => {
                    const updated = { ...current };
                    if (!next) delete updated[column.name];
                    else updated[column.name] = next;
                    return updated;
                  })
                }
              />
            </Flex>
          );
        })}
      </Flex>
      <Box
        ref={bodyReference}
        flex='1'
        minH={`${MIN_BODY_HEIGHT_PX}px`}
        position='relative'
        sx={{
          minHeight: 'max(400px, calc(100vh - 420px))',
          '& > div': { overflowX: 'auto !important' },
        }}
      >
        {rowsQuery.isLoading && (
          <Flex
            align='center'
            justify='center'
            minH={`${MIN_BODY_HEIGHT_PX}px`}
            gap={2}
            color={text.secondary}
          >
            <Spinner size='sm' color={accent.solid} />
            <Text fontSize={fontSize.body}>Loading rows…</Text>
          </Flex>
        )}
        {rowsQuery.isError && (
          <Flex align='center' justify='center' minH={`${MIN_BODY_HEIGHT_PX}px`} px={4}>
            <Text fontSize={fontSize.body} color={text.primary} role='alert'>
              The transaction data could not be loaded. Try again.
            </Text>
          </Flex>
        )}
        {!rowsQuery.isLoading && !rowsQuery.isError && flatRows.length === 0 && (
          <Flex
            align='center'
            justify='center'
            minH={`${MIN_BODY_HEIGHT_PX}px`}
            color={text.secondary}
          >
            <Text fontSize={fontSize.body}>No rows match the current filters.</Text>
          </Flex>
        )}
        {!rowsQuery.isLoading && !rowsQuery.isError && flatRows.length > 0 && (
          <FixedSizeList
            ref={listReference}
            height={visibleListHeight}
            itemCount={flatRows.length}
            itemSize={ROW_HEIGHT}
            width='100%'
            onItemsRendered={onItemsRendered}
          >
            {Row}
          </FixedSizeList>
        )}
      </Box>
      <Flex
        px={4}
        py={2}
        justify='space-between'
        borderTop={`1px solid ${neutral.border}`}
        fontSize='11px'
        color={text.secondary}
        flexShrink={0}
      >
        <Text>
          {flatRows.length.toLocaleString()}
          {totalRows !== null ? ` of ${totalRows.toLocaleString()}` : ''} rows loaded
        </Text>
        {rowsQuery.isFetchingNextPage && <Text>Loading more…</Text>}
      </Flex>
    </Box>
  );
};

export default TaskDataTable;
